import AssessmentIcon from '@mui/icons-material/Assessment';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import GroupsIcon from '@mui/icons-material/Groups';
import MarkChatUnreadIcon from '@mui/icons-material/MarkChatUnread';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import TimerIcon from '@mui/icons-material/Timer';
import {
  Avatar,
  Box,
  Chip,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import Chart from 'src/components/chart';
import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';
import Spinner from 'src/utils/Loader/spinner';
import { useToaster } from 'src/utils/toaster/toasterContext';

const defaultAnalytics = {
  summary: {},
  daily_trend: [],
  status_breakdown: [],
  agent_workload: [],
  visitor_sources: [],
};

const numberFormat = new Intl.NumberFormat('en-US');

const normalizeStatusKey = (status) => String(status || 'unknown').trim().toLowerCase() || 'unknown';

const metricCards = [
  {
    key: 'total_conversations',
    titleKey: 'total_conversations',
    icon: <ChatBubbleOutlineIcon />,
    accent: '#0f766e',
  },
  {
    key: 'open_conversations',
    titleKey: 'open_conversations',
    icon: <AssessmentIcon />,
    accent: '#2563eb',
  },
  {
    key: 'unread_messages',
    titleKey: 'unread_messages',
    icon: <MarkChatUnreadIcon />,
    accent: '#dc2626',
  },
  {
    key: 'total_visitors',
    titleKey: 'visitors',
    icon: <GroupsIcon />,
    accent: '#ca8a04',
  },
  {
    key: 'media_messages',
    titleKey: 'media_messages',
    icon: <PermMediaIcon />,
    accent: '#7c3aed',
  },
  {
    key: 'online_agents',
    titleKey: 'online_agents',
    icon: <SupportAgentIcon />,
    accent: '#16a34a',
  },
];

const SupportAnalyticsDashboard = () => {
  const { t: translate } = useTranslation();
  const [analytics, setAnalytics] = useState(defaultAnalytics);
  const [loader, setLoader] = useState(true);
  const { showToast } = useToaster();
  const theme = useTheme();

  const fetchAnalytics = useCallback(() => {
    setLoader(true);
    HttpServices.Get({
      url: userModuleURL.liveChatAnalyticsDashboard,
      successCallback: (data) => {
        setAnalytics({ ...defaultAnalytics, ...(data || {}) });
        setLoader(false);
      },
      failureCallback: (message) => {
        setLoader(false);
        showToast(
          message || translate('analytics.load_error', { defaultValue: 'Failed to load support analytics' }),
          'e'
        );
      },
    });
  }, [showToast, translate]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const trendChart = useMemo(() => {
    const labels = (analytics.daily_trend || []).map((item) => item.date);
    return {
      labels,
      series: [
        {
          name: translate('analytics.chart.conversations', { defaultValue: 'Conversations' }),
          data: (analytics.daily_trend || []).map((item) => item.conversations || 0),
        },
        {
          name: translate('analytics.chart.messages', { defaultValue: 'Messages' }),
          data: (analytics.daily_trend || []).map((item) => item.messages || 0),
        },
        {
          name: translate('analytics.chart.visitors', { defaultValue: 'Visitors' }),
          data: (analytics.daily_trend || []).map((item) => item.visitors || 0),
        },
      ],
    };
  }, [analytics.daily_trend, translate]);

  const statusChart = useMemo(() => ({
    labels: (analytics.status_breakdown || []).map((item) =>
      translate(`analytics.status.${normalizeStatusKey(item.status)}`, {
        defaultValue: item.status || translate('analytics.status.unknown', { defaultValue: 'unknown' }),
      })
    ),
    series: (analytics.status_breakdown || []).map((item) => item.count || 0),
  }), [analytics.status_breakdown, translate]);

  const maxVisitorSource = Math.max(...(analytics.visitor_sources || []).map((item) => item.count || 0), 1);

  const avgFirstResponse = analytics.summary?.avg_first_response_minutes || 0;

  return (
    <>
      <Helmet>
        <title>{translate('analytics.title', { defaultValue: 'Support Analytics' })}</title>
      </Helmet>

      {loader ? (
        <Spinner height={window.innerHeight - 110} />
      ) : (
        <Box
          sx={{
            minHeight: 'calc(100vh - 110px)',
            pb: { xs: 18, md: 16 },
            overflow: 'visible',
          }}
        >
        <Stack spacing={3}>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              borderRadius: 2,
              overflow: 'hidden',
              color: '#0f172a',
              background:
                'linear-gradient(135deg, rgba(20,184,166,0.16), rgba(37,99,235,0.12) 45%, rgba(255,255,255,0.95))',
              borderColor: 'rgba(15,118,110,0.22)',
            }}
          >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 800 }}>
                  {translate('analytics.title', { defaultValue: 'Support Analytics' })}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                  {translate('analytics.subtitle', {
                    defaultValue: 'Track live chat volume, response health, visitor activity, and agent workload.',
                  })}
                </Typography>
              </Box>
              <Chip
                icon={<TimerIcon />}
                label={translate('analytics.avg_first_response', {
                  defaultValue: 'Avg first response: {{minutes}} min',
                  minutes: avgFirstResponse.toFixed(1),
                })}
                sx={{ bgcolor: 'background.paper', alignSelf: { xs: 'flex-start', md: 'center' } }}
              />
            </Stack>
          </Paper>

          <Grid container spacing={2}>
            {metricCards.map((card) => (
              <Grid item xs={12} sm={6} lg={4} key={card.key}>
                <Paper variant="outlined" sx={{ p: 2.25, borderRadius: 2, height: '100%' }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: `${card.accent}18`, color: card.accent }}>
                      {card.icon}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        {translate(`analytics.metrics.${card.titleKey}`, { defaultValue: card.titleKey })}
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 800 }}>
                        {numberFormat.format(analytics.summary?.[card.key] || 0)}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} lg={8}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {translate('analytics.activity_trend', { defaultValue: '14-Day Activity Trend' })}
                </Typography>
                <Chart
                  type="area"
                  height={340}
                  series={trendChart.series}
                  options={{
                    chart: { toolbar: { show: false }, zoom: { enabled: false } },
                    colors: ['#2563eb', '#0f766e', '#ca8a04'],
                    dataLabels: { enabled: false },
                    stroke: { curve: 'smooth', width: 3 },
                    fill: { type: 'gradient', gradient: { opacityFrom: 0.22, opacityTo: 0.02 } },
                    grid: { borderColor: theme.palette.divider },
                    xaxis: { categories: trendChart.labels },
                    yaxis: { min: 0, forceNiceScale: true },
                    legend: { position: 'top', horizontalAlign: 'right' },
                  }}
                />
              </Paper>
            </Grid>

            <Grid item xs={12} lg={4}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {translate('analytics.conversation_status', { defaultValue: 'Conversation Status' })}
                </Typography>
                {statusChart.series.length ? (
                  <Chart
                    type="donut"
                    height={320}
                    series={statusChart.series}
                    options={{
                      labels: statusChart.labels,
                      colors: ['#22c55e', '#64748b', '#f97316', '#ef4444'],
                      legend: { position: 'bottom' },
                      plotOptions: { pie: { donut: { size: '68%' } } },
                    }}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {translate('analytics.no_conversation_data', { defaultValue: 'No conversation data yet.' })}
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} lg={7}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {translate('analytics.agent_workload', { defaultValue: 'Agent Workload' })}
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{translate('analytics.table.agent', { defaultValue: 'Agent' })}</TableCell>
                      <TableCell>{translate('analytics.table.status', { defaultValue: 'Status' })}</TableCell>
                      <TableCell align="right">
                        {translate('analytics.table.assigned', { defaultValue: 'Assigned' })}
                      </TableCell>
                      <TableCell align="right">
                        {translate('analytics.table.replies', { defaultValue: 'Replies' })}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(analytics.agent_workload || []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4}>
                          <Typography variant="body2" color="text.secondary">
                            {translate('analytics.no_agent_activity', { defaultValue: 'No agent activity yet.' })}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      analytics.agent_workload.map((agent) => (
                        <TableRow key={agent.user_id}>
                          <TableCell>
                            {agent.name || translate('analytics.table.agent', { defaultValue: 'Agent' })}
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const statusKey = normalizeStatusKey(agent.status || 'offline');
                              return (
                                <Chip
                                  size="small"
                                  label={translate(`analytics.status.${statusKey}`, {
                                    defaultValue: agent.status || 'offline',
                                  })}
                                  color={statusKey === 'online' ? 'success' : 'default'}
                                  variant={statusKey === 'online' ? 'filled' : 'outlined'}
                                />
                              );
                            })()}
                          </TableCell>
                          <TableCell align="right">{agent.assigned_conversations || 0}</TableCell>
                          <TableCell align="right">{agent.messages_sent || 0}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, height: '100%' }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {translate('analytics.visitor_sources', { defaultValue: 'Visitor Sources' })}
                </Typography>
                <Stack spacing={1.5}>
                  {(analytics.visitor_sources || []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      {translate('analytics.no_visitor_source_data', {
                        defaultValue: 'No visitor source data yet.',
                      })}
                    </Typography>
                  ) : (
                    analytics.visitor_sources.map((source) => (
                      <Box key={source.country}>
                        <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                          <Typography variant="body2">{source.country}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {source.count}
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={Math.round(((source.count || 0) / maxVisitorSource) * 100)}
                          sx={{ height: 8, borderRadius: 999 }}
                        />
                      </Box>
                    ))
                  )}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Stack>
        </Box>
      )}
    </>
  );
};

export default SupportAnalyticsDashboard;
